import { SupervisorsEmployee } from '../../interfaces/SupervisorInterface';
import { WcdpExportedInfo, WcdpPaycorPreview, WcdpRejection } from '../../api/wcdpIasExport';

/**
 * Pure helpers behind the WCDP IAS export on the Timesheet Review board (TAS-682): the row
 * chip, the crew header's "x of y exported", the export modal's initial selection, and the
 * "Generate Paycor File" confirm counts. No React, no I/O — covered by wcdpExportModel.test.ts.
 */

export type WcdpExportMap = Map<number, WcdpExportedInfo>;

/** Row chip. 'none' renders nothing (no "Not exported" chip for WCDP). */
export type WcdpChipKind = 'none' | 'unconfirmed' | 'changed' | 'exported';

export function wcdpChipKind(x: WcdpExportedInfo | undefined): WcdpChipKind {
    if (!x) return 'none';
    if (x.confirmed === false) return 'unconfirmed';
    if (x.changedSinceExport) return 'changed';
    return 'exported';
}

/** "10/17" in the viewer's local time; '' when the timestamp does not parse. */
export function wcdpShortDate(iso: string): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return `${d.getMonth() + 1}/${d.getDate()}`;
}

/** The chip's text, or null for 'none'. */
export function wcdpChipText(x: WcdpExportedInfo | undefined): string | null {
    switch (wcdpChipKind(x)) {
        case 'unconfirmed': return 'Unconfirmed – check Solomon';
        case 'changed': return 'Changed since export';
        case 'exported': return `Exported ${wcdpShortDate(x!.exportedAt)}`.trim();
        default: return null;
    }
}

/**
 * Crew header "x of y exported" over the crew's CURRENT members (a reassigned employee counts
 * on the crew they are on now). Any recorded export counts — changed / unconfirmed / no-rows
 * employees wear their own chip, but they are not "never exported".
 */
export function wcdpCrewExportCount(
    employees: Pick<SupervisorsEmployee, 'employeeID'>[],
    exported: WcdpExportMap
): { exported: number; total: number } {
    const total = employees.length;
    const done = employees.reduce((n, e) => n + (exported.has(e.employeeID) ? 1 : 0), 0);
    return { exported: done, total };
}

/** Numeric alt-ID order (the order the legacy batch modal used); non-numeric IDs sort last by text. */
export function sortByAltEmployeeID<T extends Pick<SupervisorsEmployee, 'altEmployeeID'>>(employees: T[]): T[] {
    return [...employees].sort((a, b) => {
        const na = parseInt(a.altEmployeeID || '', 10);
        const nb = parseInt(b.altEmployeeID || '', 10);
        const aNum = Number.isFinite(na);
        const bNum = Number.isFinite(nb);
        if (aNum && bNum && na !== nb) return na - nb;
        if (aNum !== bNum) return aNum ? -1 : 1;
        return (a.altEmployeeID || '').localeCompare(b.altEmployeeID || '');
    });
}

/** Opt-out: everyone starts checked except anyone already exported (the Sage picker rule). */
export function wcdpInitialSelection(
    employees: Pick<SupervisorsEmployee, 'employeeID'>[],
    exported: WcdpExportMap
): Set<number> {
    return new Set(employees.filter((e) => !exported.has(e.employeeID)).map((e) => e.employeeID));
}

/** What "Select all" ticks: the not-exported employees only — re-exporting stays a separate act. */
export function wcdpSelectableIDs(
    employees: Pick<SupervisorsEmployee, 'employeeID'>[],
    exported: WcdpExportMap
): number[] {
    return employees.filter((e) => !exported.has(e.employeeID)).map((e) => e.employeeID);
}

/**
 * Selected employees whose earlier export sent real rows — these need the "Export again"
 * confirm and `force`. A no-rows marker sent nothing to IAS and the API's guard ignores it.
 */
export function wcdpSelectedNeedingForce(selected: Set<number>, exported: WcdpExportMap): number[] {
    const ids: number[] = [];
    selected.forEach((id) => {
        const x = exported.get(id);
        if (x && !x.noRows) ids.push(id);
    });
    return ids;
}

/** After a 422: the selection minus every rejected employee ("Export the other N"). */
export function wcdpWithoutRejected(selected: Set<number>, rejections: Pick<WcdpRejection, 'employeeID'>[]): Set<number> {
    const rejected = new Set(rejections.map((r) => r.employeeID));
    return new Set(Array.from(selected).filter((id) => !rejected.has(id)));
}

export interface WcdpPaycorConfirmModel {
    includedCount: number;
    approvedNotExportedCount: number;
    /** "Last, First #123" style labels for exported employees no longer Payroll Approved. */
    exportedNotApproved: string[];
    /** Nothing to put in the file — the API would answer 422. */
    canGenerate: boolean;
}

export function wcdpPaycorConfirmModel(preview: WcdpPaycorPreview): WcdpPaycorConfirmModel {
    const includedCount = Math.max(0, Number(preview.includedCount) || 0);
    const approvedNotExportedCount = Math.max(0, Number(preview.approvedNotExportedCount) || 0);
    const exportedNotApproved = (preview.exportedNotApproved ?? []).map((x) => {
        const name = (x.name || '').trim();
        const id = x.altEmployeeID ? `#${x.altEmployeeID}` : '';
        return [name, id].filter(Boolean).join(' ') || `Employee ${x.employeeID}`;
    });
    return { includedCount, approvedNotExportedCount, exportedNotApproved, canGenerate: includedCount > 0 };
}

export const plural = (n: number, one: string, many = `${one}s`): string => `${n} ${n === 1 ? one : many}`;
